import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import type {
  AsyncHookResult,
  Experiments,
  RegisteredExperiment,
} from '@xing-com/hub';
import { asyncResult, ExperimentsContext, useVisitorId } from '@xing-com/hub';

import { useSuspenseExperimentsQuery } from './experiments-query';
import { useExperiment, useExperimentTrackingData } from './hooks';
import { getRegisteredExperiments } from './register-experiment';

const context = { useExperiment, useExperimentTrackingData, useExperiments };

const InternalContext = createContext<AsyncHookResult<Experiments>>({
  loading: true,
});

export const SplitkitStaticModule: FC<
  PropsWithChildren<{ experiments: AsyncHookResult<Experiments> }>
> = ({ children, experiments }) => {
  return (
    <InternalContext.Provider value={experiments}>
      <ExperimentsContext.Provider value={context}>
        {children}
      </ExperimentsContext.Provider>
    </InternalContext.Provider>
  );
};

export type SplitkitModuleProps = PropsWithChildren<{
  conditionalRegisteredExperiments?: RegisteredExperiment[];
}>;

export const SplitkitModule: FC<SplitkitModuleProps> = ({
  conditionalRegisteredExperiments,
  children,
}) => {
  const visitorId = useVisitorId();
  const registeredExperiments = [
    ...getRegisteredExperiments(),
    ...(conditionalRegisteredExperiments ?? []),
  ];
  const experiments = asyncResult(
    useSuspenseExperimentsQuery(visitorId, registeredExperiments)
  );

  return (
    <SplitkitStaticModule experiments={experiments}>
      {children}
    </SplitkitStaticModule>
  );
};

export function useExperiments(): AsyncHookResult<Experiments> {
  return useContext(InternalContext);
}
